import React, { useEffect, useRef, useState } from "react";
import { FaBan, FaCaretDown } from "react-icons/fa";
import { U3140_PDF_ANNOTATION_FILL_NONE, U3140_PDF_ANNOTATION_SWATCH_COLORS } from "../../U3140PdfAnnotationsPalette";

export interface U3140PdfAnnotationColorMenuButtonProps {
  /** Libellé du bouton (ex. Remplissage, Contour). */
  label: string;
  /** Libellé de l’option « aucune ». */
  noneLabel: string;
  variant: "fill" | "stroke";
  /** Couleur courante (#RRGGBB). Pour le remplissage, peut être `transparent`. */
  color: string;
  /** Contour : largeur 0 = « aucun contour ». */
  strokeWidth: number;
  disabled?: boolean;
  /** Infobulle du bouton principal (ex. préréglage requis). */
  menuButtonTitle?: string;
  onPickColor: (hex: string) => void;
  /** Remplissage : transparent ; contour : largeur 0. */
  onClear: () => void;
  /**
   * Remplissage : désactive « aucun » + pastilles (ex. ligne/flèche sans remplissage).
   * Le curseur d’opacité reste actif si fourni.
   */
  fillSwatchesDisabled?: boolean;
  /** Remplissage : libellé du curseur d’opacité. */
  opacityLabel?: string;
  opacity?: number;
  onOpacityChange?: (value: number) => void;
  opacityDisabled?: boolean;
  /** Attributs du curseur d’opacité (remplissage). */
  opacityMin?: number;
  opacityMax?: number;
  opacityStep?: number;
  /** Contour : libellé du champ épaisseur (px). */
  strokeWidthInputLabel?: string;
  onStrokeWidthInputChange?: (value: number) => void;
  strokeWidthInputDisabled?: boolean;
  strokeWidthInputMin?: number;
  strokeWidthInputMax?: number;
}

function swatchButtonClass(active: boolean): string {
  return [
    "h-7 w-7 rounded-sm border border-gray-300 shadow-xs transition hover:scale-105 focus:outline-hidden focus:ring-2 focus:ring-blue-500/40",
    active ? "ring-2 ring-blue-600 ring-offset-1" : "",
  ].join(" ");
}

export const U3140PdfAnnotationColorMenuButton: React.FC<U3140PdfAnnotationColorMenuButtonProps> = ({
  label,
  noneLabel,
  variant,
  color,
  strokeWidth,
  disabled = false,
  menuButtonTitle,
  onPickColor,
  onClear,
  fillSwatchesDisabled = false,
  opacityLabel,
  opacity,
  onOpacityChange,
  opacityDisabled = false,
  opacityMin = 0.1,
  opacityMax = 1,
  opacityStep = 0.05,
  strokeWidthInputLabel,
  onStrokeWidthInputChange,
  strokeWidthInputDisabled = false,
  strokeWidthInputMin = 0,
  strokeWidthInputMax = 12,
}) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (disabled) {
      setOpen(false);
    }
  }, [disabled]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc, true);
    return () => document.removeEventListener("mousedown", onDoc, true);
  }, [open]);

  const fillNone =
    variant === "fill" &&
    (!color || color === U3140_PDF_ANNOTATION_FILL_NONE || color.toLowerCase() === "transparent");
  const strokeNone = variant === "stroke" && strokeWidth <= 0;
  const swatchesBlocked = variant === "fill" && fillSwatchesDisabled;
  /** Pastille : symbole « aucune couleur » aussi lorsque le contrôle est désactivé (ex. préréglage requis). */
  const showNoneSymbol = fillNone || strokeNone || disabled;
  const previewSwatchTitle = disabled
    ? menuButtonTitle ?? noneLabel
    : fillNone || strokeNone
      ? noneLabel
      : color;
  const checkerboard =
    "repeating-linear-gradient(135deg, #e5e7eb 0 4px, #fff 4px 8px)" as const;

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        disabled={disabled}
        title={disabled ? menuButtonTitle ?? noneLabel : menuButtonTitle}
        onClick={() => !disabled && setOpen((o) => !o)}
        className="flex h-[46px] min-w-30 items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-xs transition hover:border-gray-400 disabled:cursor-not-allowed disabled:bg-muted disabled:opacity-60"
        aria-expanded={open}
        aria-haspopup="dialog"
      >
        <span
          className="relative flex h-6 w-6 shrink-0 items-center justify-center overflow-hidden rounded-sm border border-gray-200 bg-white"
          title={previewSwatchTitle}
        >
          {showNoneSymbol ? (
            <>
              <span className="absolute inset-0" style={{ background: checkerboard }} aria-hidden />
              <FaBan className="relative z-1 h-4 w-4 text-gray-800 drop-shadow-[0_0_1px_rgba(255,255,255,0.95)]" aria-hidden />
            </>
          ) : (
            <span className="absolute inset-0 rounded-[3px]" style={{ backgroundColor: color }} aria-hidden />
          )}
        </span>
        <span className="flex-1 truncate text-left font-medium text-gray-800">{label}</span>
        <FaCaretDown className={`shrink-0 text-gray-500 transition ${open ? "rotate-180" : ""}`} />
      </button>

      {open ? (
        <div
          className="absolute left-0 top-[calc(100%+4px)] z-10000 min-w-[220px] rounded-lg border border-gray-200 bg-white p-3 shadow-xl"
          role="dialog"
        >
          <button
            type="button"
            disabled={swatchesBlocked}
            className="mb-3 w-full rounded-sm border border-gray-300 bg-white px-3 py-2 text-left text-sm font-medium text-gray-800 hover:bg-gray-50 disabled:cursor-not-allowed disabled:bg-muted disabled:opacity-50"
            onClick={() => {
              if (swatchesBlocked) return;
              onClear();
              setOpen(false);
            }}
          >
            {noneLabel}
          </button>
          <div className={`grid grid-cols-6 gap-1.5 ${swatchesBlocked ? "pointer-events-none opacity-40" : ""}`}>
            {U3140_PDF_ANNOTATION_SWATCH_COLORS.map((hex) => {
              const active =
                variant === "fill"
                  ? !fillNone && color.toLowerCase() === hex.toLowerCase()
                  : strokeWidth > 0 && color.toLowerCase() === hex.toLowerCase();
              return (
                <button
                  key={hex}
                  type="button"
                  disabled={swatchesBlocked}
                  className={swatchButtonClass(active)}
                  style={{ backgroundColor: hex }}
                  title={hex}
                  onClick={() => {
                    onPickColor(hex);
                    setOpen(false);
                  }}
                />
              );
            })}
          </div>
          {variant === "fill" && onOpacityChange != null && opacityLabel != null && opacity != null ? (
            <label className="mt-3 flex flex-col gap-1 border-t border-gray-100 pt-3 text-xs text-gray-600">
              <div className="flex items-center justify-between gap-2">
                <span>{opacityLabel}</span>
                <span className="shrink-0 text-gray-600 tabular-nums">
                  {Math.round(opacity * 100)} %
                </span>
              </div>
              <input
                type="range"
                min={opacityMin}
                max={opacityMax}
                step={opacityStep}
                value={opacity}
                disabled={opacityDisabled}
                onChange={(e) => onOpacityChange(Number(e.target.value))}
              />
            </label>
          ) : null}
          {variant === "stroke" &&
          onStrokeWidthInputChange != null &&
          strokeWidthInputLabel != null ? (
            <label className="mt-3 flex flex-col gap-1 border-t border-gray-100 pt-3 text-xs text-gray-600">
              <span>{strokeWidthInputLabel}</span>
              <input
                type="text"
                inputMode="decimal"
                className="w-full max-w-32 rounded-sm border px-2 py-1 text-sm"
                value={strokeWidth}
                disabled={strokeWidthInputDisabled}
                onChange={(e) => {
                  const t = e.target.value.trim().replace(",", ".");
                  if (t === "") {
                    onStrokeWidthInputChange(strokeWidthInputMin);
                    return;
                  }
                  const n = Number(t);
                  if (Number.isNaN(n)) {
                    return;
                  }
                  onStrokeWidthInputChange(
                    Math.min(strokeWidthInputMax, Math.max(strokeWidthInputMin, n))
                  );
                }}
              />
            </label>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};
